import React, { KeyboardEvent } from 'react';
import {
  CreateProductFieldKeys,
  CreateProductModalProps,
  FieldProps,
} from 'enums/CreateProductModalEnums';
import styles from './CreateProductModal.module.scss';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { CategoryOptions } from './ProductModalForm';
import { LABEL } from './CreateProductModalConstants';

interface CreateProductModalFieldProps {
  brandOptions: CategoryOptions[];
  temperatureControl: CategoryOptions[];
  ml: CategoryOptions[];
  processingFacilityOptions: CategoryOptions[];
  handleOpenCreateBrandModal: () => void;
  handleRedirectToLocations: () => void;
  openHelpDeskTooltip: (input: HelpDeskInputFieldEnum) => void;
  disabled: boolean;
  fieldsMap?: Map<string, boolean>;
  products?: CategoryOptions[];
  handleChangeProductName: (value: string) => Promise<void>;
  handleSelect: (event: KeyboardEvent<HTMLInputElement>) => void;
  isCreate: boolean;
  showSearch: boolean;
  isEdit: boolean;
  handleChangePF: (value: string) => Promise<void>;
}

export const getCreateProductModalFields = ({
  brandOptions,
  temperatureControl,
  ml,
  processingFacilityOptions,
  handleOpenCreateBrandModal,
  openHelpDeskTooltip,
  handleRedirectToLocations,
  disabled,
  fieldsMap,
  products,
  handleChangeProductName,
  handleSelect,
  isCreate,
  showSearch,
  isEdit,
  handleChangePF,
}: CreateProductModalFieldProps) => {
  const isFieldDisabled = (fieldKey: string): boolean => {
    if (disabled) return true;
    else if (fieldKey === CreateProductFieldKeys.NAME && isCreate) return false;
    else if (!fieldsMap?.size) return false;
    return !fieldsMap?.get(fieldKey);
  };

  const handleFilter = (optionA: CategoryOptions, optionB: CategoryOptions) => {
    return (optionA?.label ?? '')
      .toLowerCase()
      .localeCompare((optionB?.label ?? '').toLowerCase());
  };

  return {
    fields: [
      {
        key: CreateProductFieldKeys.NAME,
        name: FieldProps.PRODUCT_NAME,
        placeholder: FieldProps.PRODUCT_NAME_PLACEHOLDER,
        label: FieldProps.PRODUCT_NAME_PLACEHOLDER,
        options: products,
        select: !isEdit,
        date: false,
        size: FieldProps.SIZE_BIG,
        onChange: handleChangeProductName,
        onFilter: handleFilter,
        onKeyUp: handleSelect,
        onKeyDown: handleSelect,
        optionFilterProp: LABEL,
        showSearch: showSearch,
        disabled: isFieldDisabled(CreateProductFieldKeys.NAME),
      },

      {
        key: CreateProductFieldKeys.BRAND_ID,
        name: FieldProps.BRAND,
        placeholder: FieldProps.BRAND_PLACEHOLDER,
        label: FieldProps.BRAND_PLACEHOLDER,
        select: true,
        date: false,
        size: FieldProps.SIZE_BIG,
        options: brandOptions,
        disabled: isFieldDisabled(CreateProductFieldKeys.BRAND_ID),
        render: (menu: React.ReactNode) => {
          return (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleOpenCreateBrandModal}>
                <h3 className={styles.addIcon}>
                  <Add />
                </h3>
                <p>{CreateProductModalProps.CREATE_BRAND}</p>
              </div>
              {menu}
            </>
          );
        },
      },
      {
        key: CreateProductFieldKeys.NET_WEIGHT,
        name: FieldProps.NET_WEIGHT,
        placeholder: FieldProps.NET_WEIGHT_PLACEHOLDER,
        label: FieldProps.NET_WEIGHT_PLACEHOLDER,
        select: false,
        date: false,
        size: FieldProps.SIZE_MEDIUM,
        info: true,
        disabled: isFieldDisabled(CreateProductFieldKeys.NET_WEIGHT),
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.netWeight);
        },
      },
      {
        key: CreateProductFieldKeys.NET_WEIGHT_UNIT,
        name: FieldProps.UNIT,
        placeholder: FieldProps.UNIT_PLACEHOLDER,
        label: FieldProps.UNIT_PLACEHOLDER,
        select: true,
        date: false,
        size: FieldProps.SIZE_SMALL,
        options: ml,
        disabled: isFieldDisabled(CreateProductFieldKeys.NET_WEIGHT_UNIT),
      },
      {
        key: CreateProductFieldKeys.GROSS_WEIGHT,
        name: FieldProps.GROSS_WEIGHT,
        placeholder: FieldProps.GROSS_WEIGHT_LABEL,
        label: (
          <p>
            Product gross weight <span style={{ fontWeight: '900' }}>(g)</span>{' '}
            (Including packaging)
          </p>
        ),
        select: false,
        date: false,
        size: FieldProps.SIZE_BIG,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.grossWeight);
        },
        disabled: isFieldDisabled(CreateProductFieldKeys.GROSS_WEIGHT),
      },
      {
        key: CreateProductFieldKeys.TEMPERATURE_CONTROL,
        name: FieldProps.TEMPERATURE_CONTROL,
        placeholder: FieldProps.TEMPERATURE_CONTROL_PLACEHOLDER,
        label: FieldProps.TEMPERATURE_CONTROL_PLACEHOLDER,
        select: true,
        date: false,
        size: FieldProps.SIZE_BIG,
        options: temperatureControl,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.temperatureControl);
        },
        disabled: isFieldDisabled(CreateProductFieldKeys.TEMPERATURE_CONTROL),
      },
      {
        key: CreateProductFieldKeys.DENSITY,
        name: FieldProps.DENSITY,
        placeholder: FieldProps.DENSITY_PLACEHOLDER,
        label: FieldProps.DENSITY_PLACEHOLDER,
        select: false,
        date: false,
        size: FieldProps.SIZE_BIG,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.productDensity);
        },
        disabled: isFieldDisabled(CreateProductFieldKeys.DENSITY),
      },
      {
        key: CreateProductFieldKeys.DESCRIPTION,
        name: FieldProps.DESCRIPTION,
        placeholder: FieldProps.Prod_Description,
        label: FieldProps.Desc,
        select: false,
        date: false,
        size: FieldProps.SIZE_EXTRA_BIG,
        disabled: isFieldDisabled(CreateProductFieldKeys.DESCRIPTION),
      },
    ],
    processingFacilityFields: [
      {
        key: CreateProductFieldKeys.PROCESSING_FACILITY_ID,
        name: FieldProps.PROCESSING_FACILITY,
        placeholder: FieldProps.PROCESSING_FACILITY_PLACEHOLDER,
        label: FieldProps.PROCESSING_FACILITY_PLACEHOLDER,
        select: true,
        date: false,
        size: FieldProps.SIZE_BIG,
        info: true,
        onChange: handleChangePF,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.processingFacilityName);
        },
        options: processingFacilityOptions,
        render: (menu: React.ReactNode) => {
          return (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleRedirectToLocations}>
                <h3 className={styles.addIcon}>
                  <Add />
                </h3>
                <p>{CreateProductModalProps.CREATE_PROCESSING_FACILITY}</p>
              </div>
              {menu}
            </>
          );
        },
        disabled: isFieldDisabled(
          CreateProductFieldKeys.PROCESSING_FACILITY_ID
        ),
      },
      {
        key: CreateProductFieldKeys.PROCESSING_FACILITY_COST_PERCENTAGE,
        name: FieldProps.PERCENTAGE_COST,
        placeholder: FieldProps.PERCENTAGE_PLACEHOLDER,
        label: FieldProps.PERCENTAGE_PLACEHOLDER,
        select: false,
        date: false,
        size: FieldProps.SIZE_BIG,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.productCOGSRatio);
        },
        disabled: isFieldDisabled(
          CreateProductFieldKeys.PROCESSING_FACILITY_COST_PERCENTAGE
        ),
      },
      {
        key: CreateProductFieldKeys.REPORTING_YEAR,
        name: FieldProps.REPORTING_YEAR,
        placeholder: FieldProps.REPORTING_YEAR_PLACEHOLDER,
        label: FieldProps.REPORTING_YEAR_PLACEHOLDER,
        select: false,
        date: true,
        size: FieldProps.SIZE_BIG,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.reportingPeriod);
        },
        disabled: isFieldDisabled(CreateProductFieldKeys.REPORTING_YEAR),
      },
      {
        key: CreateProductFieldKeys.REPORTING_PERIOD_UNITS,
        name: FieldProps.PERIOD_PRODUCTION,
        placeholder: FieldProps.PERIOD_PRODUCT_PLACEHOLDER,
        label: FieldProps.PERIOD_PRODUCT_PLACEHOLDER,
        select: false,
        date: false,
        size: FieldProps.SIZE_BIG,
        info: true,
        handleOpenModal: () => {
          openHelpDeskTooltip(HelpDeskInputFieldEnum.reportingPeriodProduction);
        },
        disabled: isFieldDisabled(
          CreateProductFieldKeys.REPORTING_PERIOD_UNITS
        ),
      },
    ],
  };
};
